// src/pages/MyBody.tsx — body_metrics chart over time + log-new form.
// The recent-entries table is the SDK's <BodyMetrics/>; the chart and the
// log-new form stay app-local (the SDK does not own write surfaces).
import { useState } from "react";
import { BodyMetrics } from "@goodbetter/pulse-sdk";
import { useBodyMetrics, useCreateBodyMetric } from "@/lib/queries";
import { Card, PageHeader, Spinner, EmptyState, Button, ErrorBanner } from "@/components/ui";
import { WeightChart } from "@/components/WeightChart";

export function MyBody() {
  const { data, isLoading, error } = useBodyMetrics();
  const create = useCreateBodyMetric();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [weight, setWeight] = useState("");
  const [bodyFat, setBodyFat] = useState("");

  const items = data?.items ?? [];

  function submit(e: React.FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        date,
        weightLbs: weight ? Number(weight) : undefined,
        bodyFatPct: bodyFat ? Number(bodyFat) : undefined,
        source: "manual",
      } as never,
      {
        onSuccess: () => {
          setWeight("");
          setBodyFat("");
        },
      }
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="My Body" subtitle="Body composition over time." />

      {error ? <ErrorBanner message={(error as Error).message} /> : null}

      <Card>
        <h2 className="mb-3 text-sm font-medium text-ls-fg">Weight (lbs)</h2>
        {isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <EmptyState title="No metrics logged yet" hint="Use the form below to add your first entry." />
        ) : (
          <WeightChart
            points={[...items]
              .filter((m) => m.weightLbs != null)
              .reverse()
              .map((m) => ({ date: m.date, value: m.weightLbs as number }))}
          />
        )}
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-medium text-ls-fg">Log a new entry</h2>
        {create.isError ? (
          <div className="mb-3">
            <ErrorBanner message={(create.error as Error).message} />
          </div>
        ) : null}
        <form className="grid grid-cols-1 gap-3 sm:grid-cols-4" onSubmit={submit}>
          <input
            aria-label="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
          <input
            aria-label="weight"
            type="number"
            step="0.1"
            placeholder="Weight (lbs)"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
          <input
            aria-label="body fat"
            type="number"
            step="0.1"
            placeholder="Body fat %"
            value={bodyFat}
            onChange={(e) => setBodyFat(e.target.value)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Add entry"}
          </Button>
        </form>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-medium text-ls-fg">Recent entries</h2>
        <BodyMetrics limit={10} />
      </Card>
    </div>
  );
}
